import { z } from 'zod';
import { optionalFinanceCurrencySchema } from './finance-currency.schema.js';

/** Populated cause/consequence refs from API may include null optional fields */
const riskRegisterRefDocSchema = z.object({
    _id: z.string().nullish(),
    id: z.string().nullish(),
    name: z.string().nullish(),
    description: z.string().nullish()
});

export const RiskRegisterSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.string().optional(),
    tenantId: z.string().optional(),
    riskId: z.string().optional(),
    title: z.string().min(1, "form.validation.titleRequired").max(255),
    description: z.string().min(1, "form.validation.descriptionRequired"),
    riskNature: z.enum(['risk', 'opportunity']).optional(),
    partyInstanceId: z.string().optional(),
    priority: z.string().optional(),
    riskLevel: z.string().optional(),
    score: z.number().optional(),
    impact: z.string().optional(),
    probability: z.string().optional(),
    category: z.string().optional(),
    status: z.enum(['Open', 'Closed']).optional(),
    probabilityLevelId: z.string().optional(),
    impactLevelId: z.string().optional(),
    residualLikelihoodLevelId: z.string().optional(),
    residualImpactLevelId: z.string().optional(),
    scoreHistory: z.array(z.object({
        date: z.union([z.date(), z.string()]).optional(),
        score: z.number().nullish(),
        residualScore: z.number().nullish(),
        changedBy: z.any().optional(),
        source: z.string().optional(),
    })).optional(),
    responseStrategy: z.string().optional(),
    responseDescription: z.string().optional(),
    financialImpact: z.number().or(z.string()).optional(),
    annualizedLoss: z.number().or(z.string()).optional(),
    currency: optionalFinanceCurrencySchema,
    causes: z.array(z.union([z.string(), riskRegisterRefDocSchema])).optional(),
    consequences: z.array(z.union([z.string(), riskRegisterRefDocSchema])).optional(),
    operationalImpact: z.string().optional(),
    financialImpactRange: z.string().optional(),
    reputationalImpact: z.string().optional(),
    residualLikelihood: z.string().optional(),
    residualImpact: z.string().optional(),
    residualScore: z.number().optional(),
    customFields: z.record(z.unknown()).optional(),
    historyLog: z.array(z.object({
        _id: z.any().optional(),
        date: z.union([z.date(), z.string()]).optional(),
        score: z.number().nullish(),
        residualScore: z.number().nullish(),
        changedBy: z.any().optional(),
        note: z.string().nullish()
    })).optional(),
    createdAt: z.union([z.date(), z.string()]).optional(),
    updatedAt: z.union([z.date(), z.string()]).optional(),
});

export const CreateRiskRegisterSchema = RiskRegisterSchema.omit({
    id: true,
    _id: true,
    createdAt: true,
    updatedAt: true
}).extend({
    assetId: z.string().optional(),
});

function stripTopLevelNullKeys(data) {
    if (!data || typeof data !== "object" || Array.isArray(data)) return data;
    const o = { ...data };
    for (const k of Object.keys(o)) {
        if (o[k] === null) delete o[k];
    }
    return o;
}

/** Partial update: drop null keys; allow legacy numeric impact/probability from older records */
export const UpdateRiskRegisterSchema = z.preprocess(
    stripTopLevelNullKeys,
    RiskRegisterSchema.partial().extend({
        impact: z.union([z.string(), z.number()]).optional(),
        probability: z.union([z.string(), z.number()]).optional()
    })
);

// Frontend Form Schema
export const RiskRegisterFormSchema = z.object({
    title: z.string().min(1, "form.validation.titleRequired"),
    description: z.string().min(1, "form.validation.descriptionRequired"),
    riskNature: z.enum(['risk', 'opportunity']).optional(),
    priority: z.string().min(1, "form.validation.priorityRequired"),
    score: z.number().optional(),
    impact: z.string().min(1, "form.validation.impactRequired"),
    probability: z.string().min(1, "form.validation.likelihoodRequired"),
    responseStrategy: z.string().optional(),
    responseDescription: z.string().optional(),
    financialImpact: z.number().optional(),
    annualizedLoss: z.number().optional(),
    currency: optionalFinanceCurrencySchema,
});